import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Image } from "react-bootstrap";
import { useNavigate, useLocation, useNavigationType } from "react-router-dom";
import axios from "axios";
import { notifyError, notifySuccess } from "../components/notify";
import { API_BASE_URL, BASIC_AUTH, getCurrentLocation } from "../constants";
import ButtonSpinner from "../components/Spinner/ButtonSpinner";
import NewOrderCard from "../components/NewOrderCard";

const DeliveryDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType(); // POP, PUSH, REPLACE
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [readyOrder, setReadyOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [deliveryRemarkModal, setDeliveryRemarkModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [remarkError, setRemarkError] = useState("");
  const [imageError, setImageError] = useState("");

  const [remark, setRemark] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [otp, setOTP] = useState(null);

  const [paymentMethod, setPaymentMethod] = useState("O");
  const [cashAmount, setCashAmount] = useState("");
  const [onlineAmount, setOnlineAmount] = useState("");
  const [onlineError, setOnlineError] = useState("");
  const [cashError, setCashError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpLoading, setOtpLoading] = useState("");
  const [otpVerify, setOTPVerify] = useState("N");
  const [showOtp, setShowOtp] = useState(false);

  const [showPending, setShowPending] = useState(true);

  const no_ref = useRef(null);
  const otpRef = useRef(null);
  const onlineRef = useRef(null);

  useEffect(() => {
    if (location.state?.flag === "show") {
      setShowLogoutModal(true);
    }
  }, []);

  const handleModalEntered = () => {
    const remainingAmount = Number(selectedOrder?.sale_remainingamount || 0);

    setTimeout(() => {
      if (remainingAmount > 0) {
        if (paymentMethod === "O" && onlineRef.current) {
          onlineRef.current.focus();
        }
      } else {
        if (otpRef.current) {
          otpRef.current.blur(); // First blur
          otpRef.current.focus(); // Then focus
        }
      }
    }, 100);
    setOTPVerify("N");
    setOnlineAmount(null);
    setCashAmount(null);
  };

  useEffect(() => {
    if (navigationType === "POP") {
      // Browser Back Button
      setShowLogoutModal(false);
    }
  }, [navigationType]);

  const logout = () => {
    setShowLogoutModal(false);
    localStorage.removeItem("loginuser");
    sessionStorage.removeItem("loginuser");
    localStorage.removeItem("user");
    localStorage.removeItem("company");
    navigate("/signin", { replace: true });
  };

  const closeModal = () => {
    setShowLogoutModal(false);
  };

  useEffect(() => {
    if (showLogoutModal) {
      setTimeout(() => {
        if (no_ref.current) {
          no_ref.current.focus();
        }
      }, 50); // small delay so modal DOM is ready
    }
  }, [showLogoutModal]);

  const userStr = localStorage.getItem("user");
  const user = userStr ? JSON.parse(userStr) : null;

  const companyStr = localStorage.getItem("company");
  const company = companyStr ? JSON.parse(companyStr) : null;

  // pull orders
  const getReadyDeliveryOrders = useCallback(async () => {
    setLoading(true);

    try {
      const response = await axios.get(
        `${API_BASE_URL}/delivery/pull_ready_for_delivery_orders?companycode=${company.companycode}&delivery_agent_code=${user.delivery_agents_code}`,
        {
          headers: {
            Authorization: BASIC_AUTH,
            "Content-Type": "application/json",
          },
        }
      );

      const result = response.data;

      setReadyOrder(result || []);
    } catch (error) {
      console.error("API Error:", error);
      notifyError(error.Message || "Failed to fetch ready delivery orders");
      setReadyOrder([]);
    } finally {
      setLoading(false);
    }
  }, [company?.companycode, user.delivery_agents_code]);

  useEffect(() => {
    if (company?.companycode && user?.delivery_agents_code) {
      getReadyDeliveryOrders();
    }
  }, [
    getReadyDeliveryOrders,
    company?.companycode,
    user?.delivery_agents_code,
  ]);

  const handleDeliveryModal = async (order) => {
    setShowDeliveryModal(true);
    setSelectedOrder(order);
  };

  const handlePickupModal = async (order) => {
    setShowPickupModal(true);
    setSelectedOrder(order);
  };

  // order pickup api
  const handleProceedOrder = async (order) => {
    try {
      const payload = {
        companycode: order.companycode,
        ordercode: order.ordercode,
        delivery_agent_code: user.delivery_agents_code,
      };

      const response = await axios.post(
        `${API_BASE_URL}/delivery/pickup_start_delivery`,
        payload,
        {
          headers: {
            Authorization: BASIC_AUTH,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        notifySuccess(response.data.message);
        getReadyDeliveryOrders();
      } else {
        notifyError(`${response.data.message}`);
      }
    } catch (error) {
      console.error("Accept Order Error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to accept order. Please try again.";

      notifyError(errorMessage);
    }
  };

  const handleAcceptModal = async (order) => {
    setShowAcceptModal(true);
    setSelectedOrder(order);
  };
  const handleAcceptOrder = async (order) => {
    try {
      const payload = {
        companycode: order.companycode,
        ordercode: order.ordercode,
        delivery_agent_code: user.delivery_agents_code,
      };

      const response = await axios.post(
        `${API_BASE_URL}/delivery/accept_order_delivery`,
        payload,
        {
          headers: {
            Authorization: BASIC_AUTH,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        notifySuccess(response.data.message);
        getReadyDeliveryOrders();
      } else {
        notifyError(`${response.data.message}`);
      }
    } catch (error) {
      console.error("Accept Order Error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to accept order. Please try again.";

      notifyError(errorMessage);
    }
  };

  const acceptedOrders = readyOrder.filter(
    (order) => order.delivery_pick_up_status === "Y"
  );

  const pendingOrders = readyOrder.filter(
    (order) => order.delivery_pick_up_status === "N"
  );

  const [showAccepted, setShowAccepted] = useState(true);

  const toggleAccepted = () => {
    setShowAccepted((prev) => !prev);
  };

  const togglePending = () => {
  setShowPending((prev) => !prev);
};

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    setOnlineError("");
    setCashError("");
    // Reset amounts when changing payment method
    if (method !== "M") {
      setCashAmount("");
      setOnlineAmount("");
    }
  };

  const handleSendOtp = async () => {
    try {
      const payload = {
        companycode: selectedOrder.companycode,
        ordercode: selectedOrder.ordercode,
        customer_mobile_no: selectedOrder.customer_mobile_no,
        customer_name: selectedOrder.customername,
        order_amount: selectedOrder.sale_totalamount,
      };

      const response = await axios.post(
        `${API_BASE_URL}/delivery/resend_order_delivery_otp`,
        payload,
        {
          headers: {
            Authorization: BASIC_AUTH,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.success) {
        notifySuccess(response.data.message);
        localStorage.setItem("delivery_otp", response.data.order_otp_code);
      } else {
        notifyError(`${response.data.message}`);
      }
    } catch (error) {
      console.error("Accept Order Error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to accept order. Please try again.";

      notifyError(errorMessage);
    }
  };

  const handleCloseDeliverySubmit = () => {
    setDeliveryRemarkModal(false);
    setCashAmount(null);
    setOnlineAmount(null);
    setOTP(null);
    setRemark("");
    setImageFile(null);
    setPreview(null);
    setOTPVerify("N");

    setCashError("");
    setOnlineError("");
    setOtpError("");
    setImageError("");
    setRemarkError("");
  };

  const handleVerifyOTP = () => {
    // Check OTP entered
    if (!otp) {
      setOtpError("Please enter OTP.");
      return;
    }

    // Get stored OTP
    const storedOtp = localStorage.getItem("delivery_otp");

    // Check OTP exists in localStorage
    if (!storedOtp) {
      setOtpError("OTP expired. Please resend OTP.");
      return;
    }

    // Compare OTPs
    if (otp === storedOtp) {
      setOtpError("");
      notifySuccess("OTP verified successfully"); // 🔔 success toast
      setOTPVerify("Y");
    } else {
      setOtpError("Please enter valid OTP");
      setOTPVerify("N");
    }

    setOtpLoading(false);
  };

  useEffect(() => {
    if (deliveryRemarkModal && selectedOrder) {
      localStorage.setItem("delivery_otp", selectedOrder.otp);
    }
  }, [deliveryRemarkModal, selectedOrder]);

  const handleDeliveryRemarkOrder = (order) => {
    setSelectedOrder(order);
    setRemark("");
    setImageFile(null);
    setPreview(null);
    setDeliveryRemarkModal(true);
    setImageError("");
    setRemarkError("");
    setOTP("");
    setCashError("");
    setOnlineError("");
    setOtpError("");
    setOTPVerify("N");
    setOnlineAmount("");
    setCashAmount("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleSubmitDeliveryRemark = async () => {
    setImageError("");
    setRemarkError("");
    setOnlineError("");
    setCashError("");
    setOtpError("");

    if (
      selectedOrder?.sale_remainingamount > 0 &&
      paymentMethod === "O" &&
      !onlineAmount
    ) {
      setOnlineError("Please Enter Online Amount.");
      return;
    } else if (
      selectedOrder?.sale_remainingamount > 0 &&
      paymentMethod === "C" &&
      !cashAmount
    ) {
      setCashError("Please Enter Cash Amount.");
      return;
    } else if (
      selectedOrder?.sale_remainingamount > 0 &&
      paymentMethod === "M" &&
      !cashAmount &&
      !onlineAmount
    ) {
      setOnlineError("Please Enter Online Amount.");
      setCashError("Please Enter Cash Amount.");
      return;
    } else if (!otp) {
      setOtpError("Please Enter OTP.");
      return;
    } else if (!imageFile) {
      setImageError("Please Add Image");
      return;
    } else if (!remark) {
      setRemarkError("Please Enter Remark.");
      return;
    }
    const totalPaidAmount =
      parseFloat(onlineAmount || 0) + parseFloat(cashAmount || 0);

    const totalAmount = parseFloat(selectedOrder.sale_remainingamount);

    if (Math.abs(totalPaidAmount - totalAmount) > 0.01 && paymentMethod !== "B") {
      notifyError("Please enter valid amount");
      return;
    }

    const customerOrderCode = selectedOrder.sale_details[0].customerordercode;

    try {
      const formData = new FormData();

      formData.append("companycode", selectedOrder.companycode);
      formData.append("ordercode", selectedOrder.ordercode);
      formData.append("delivery_agent_code", user.delivery_agents_code);
      formData.append("delivery_remark", remark || "");
      formData.append(
        "update_sale_details",
        selectedOrder?.sale_remainingamount > 0 ? "Y" : "N"
      );
      formData.append("paymode", paymentMethod);
      formData.append("online", onlineAmount || 0);
      formData.append("cash", cashAmount || 0);
      formData.append("customerordercode", customerOrderCode);
      formData.append("customercode", selectedOrder.customercode);

      // SEND FILE DIRECTLY
      if (imageFile) {
        formData.append("delivery_image", imageFile);
      }
      // WAIT for location
      try {
        const { latitude, longitude } = await getCurrentLocation();

        formData.append("delivered_location", `${latitude},${longitude}`);
      } catch (locError) {
        console.error("Location error:", locError);

        // Fallback to delivery_location from selectedOrder
        if (selectedOrder?.delivery_location) {
          formData.append(
            "delivered_location",
            selectedOrder.delivery_location
          );
        } else {
          formData.append("delivered_location", "0,0");
        }
      }

      const res = await axios.post(
        `${API_BASE_URL}/delivery/delivery_over`,
        formData,
        {
          headers: {
            Authorization: BASIC_AUTH,
          },
        }
      );

      if (res.data.success) {
        notifySuccess(res.data.message || "Delivery details saved");
        setDeliveryRemarkModal(false);
        setShowSuccessModal(true);
      } else {
        notifyError(res.data.message || "Failed to save delivery details");
      }
    } catch (error) {
      console.error("Accept Order Error:", error);

      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save delivery details. Please try again.";

      notifyError(errorMessage);
    }
  };

  return (
    <>
      <div className="d-flex flex-column align-items-center justify-content-center mb-3 mt-2">
        <ButtonSpinner
          type="submit"
          className="col-lg-2 col-md-3 col-5  btn-theme btn-md text-black"
          loading={loading}
          onClick={getReadyDeliveryOrders}
        >
          <b>
            {!loading && <i className="fa-solid fa-arrows-rotate me-2"></i>}
            {loading ? "Loading Data..." : "Pull Order"}
          </b>
        </ButtonSpinner>
      </div>

      {!loading && (
        <>
          <hr className="text-white m-0" />
          <div className="container-fluid">
            <div className="row">
              {/*  Accepted Orders Section */}
              {acceptedOrders.length > 0 && (
                <>
                  <div
                    className="col-12 d-flex justify-content-start align-items-center text-theme mb-2"
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleAccepted()}
                  >
                    <h5 className="m-0">
                      My Accepted Orders ({acceptedOrders.length})
                    </h5>

                    <span style={{ marginLeft: "8px" }}>
                      {showAccepted ? (
                        <i className="material-icons font-weight-bolder mt-2">
                          expand_less
                        </i>
                      ) : (
                        <i className="material-icons font-weight-bolder mt-2">
                          expand_more
                        </i>
                      )}
                    </span>
                  </div>

                  {showAccepted &&
                    acceptedOrders.map((order) => {
                      // each order is already identified by order.ordercode
                      const isStarted = order.start_delivery_status === "Y";

                      return (
                        <NewOrderCard
                          key={order.ordercode}
                          order={order}
                          buttonLable={
                            isStarted ? "Mark as Delivered" : "Order Pickup"
                          }
                          buttonClass={
                            isStarted ? "btn btn-success" : "btn btn-primary"
                          }
                          onAccept={(o) =>
                            isStarted
                              ? handleDeliveryModal(o)
                              : handlePickupModal(o)
                          }
                        />
                      );
                    })}
                </>
              )}
              {acceptedOrders.length > 0 && <hr className="text-white my-0" />}
            
              {/* Pending Orders Section */}
{pendingOrders.length > 0 && (
  <>
    <div
      className="col-12 d-flex justify-content-start align-items-center text-theme mt-2 mb-2"
      style={{ cursor: "pointer" }}
      onClick={togglePending}
    >
      <h5 className="m-0">
        Available Orders ({pendingOrders.length})
      </h5>

      <span style={{ marginLeft: "8px" }}>
        {showPending ? (
          <i className="material-icons font-weight-bolder mt-2">
            expand_less
          </i>
        ) : (
          <i className="material-icons font-weight-bolder mt-2">
            expand_more
          </i>
        )}
      </span>
    </div>

    {/* THIS IS THE FIX */}
    {showPending &&
      pendingOrders.map((order) => (
        <NewOrderCard
          key={order.ordercode}
          order={order}
          buttonLable="Accept Order"
          buttonClass="accept-btn"
          onAccept={(o) => handleAcceptModal(o)}
        />
      ))}
  </>
)}

              {/* No Orders */}
              {acceptedOrders.length === 0 && pendingOrders.length === 0 && (
                <div className="col-12 text-center text-white mt-2">
                  <strong>No orders available</strong>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <Modal
        show={showAcceptModal}
        onHide={() => setShowAcceptModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title>Accept Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to accept order?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              onClick={() => setShowAcceptModal(false)}
              className="no-btn"
            >
              No
            </Button>
            <Button
              variant="success"
              className="btn btn-success  "
              onClick={() => {
                setShowAcceptModal(false);
                handleAcceptOrder(selectedOrder);
              }}
            >
              Yes
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={showPickupModal}
        onHide={() => setShowPickupModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title>Pickup Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to pickup order?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              onClick={() => setShowPickupModal(false)}
              className="no-btn"
            >
              No
            </Button>
            <Button
              variant="success"
              className="btn btn-success  "
              onClick={() => {
                setShowPickupModal(false);
                handleProceedOrder(selectedOrder);
              }}
            >
              Yes
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={showDeliveryModal}
        onHide={() => setShowDeliveryModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title className="text-theme">Mark as Delivered</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to delivery for order?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              onClick={() => setShowDeliveryModal(false)}
              className="no-btn"
            >
              No
            </Button>
            <Button
              variant="success"
              className="btn btn-success  "
              onClick={() => {
                setShowDeliveryModal(false);
                handleDeliveryRemarkOrder(selectedOrder);
              }}
            >
              Yes
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={deliveryRemarkModal}
        onHide={() => handleCloseDeliverySubmit()}
        backdrop="static"
        keyboard={false}
        centered
        onEntered={handleModalEntered}
        size="lg"
      >
     <div className="bg-Modal-black">
          <Modal.Header closeButton className="py-1">
            <Modal.Title className="d-block">
              <div className="text-theme">Delivery Confirmation</div>

              <div className="fs-6">
                {selectedOrder?.sale_remainingamount > 0 && (
                  <>
                    <span className="order-number text-success">
                      Total Amount:
                    </span>
                    <span className="amount-section text-success ms-2">
                      ₹{selectedOrder?.sale_totalamount}
                    </span>
                  </>
                )}
              </div>

              {selectedOrder?.sale_remainingamount > 0 && (
                <div className="fs-6">
                  <span className="red-error">Bal Amt:</span>
                  <span className="bal-amount-section ms-2">
                    ₹{selectedOrder?.sale_remainingamount}
                  </span>
                </div>
              )}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            {/* Image Upload */}
            <div className="mb-3">
              {selectedOrder?.sale_remainingamount > 0 && (
                <>
                  <div className="mb-3 d-md-flex">
                    <label className="form-label fw-bold col-md-5 ">
                      Payment Method : <span className="red-error"> *</span>
                    </label>
                    <div className="d-flex flex-wrap gap-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="paymentOnline"
                          value="O"
                          checked={paymentMethod === "O"}
                          onChange={() => handlePaymentMethodChange("O")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="paymentOnline"
                        >
                          Online
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="paymentCash"
                          value="C"
                          checked={paymentMethod === "C"}
                          onChange={() => handlePaymentMethodChange("C")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="paymentCash"
                        >
                          Cash
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="paymentBoth"
                          value="M"
                          checked={paymentMethod === "M"}
                          onChange={() => handlePaymentMethodChange("M")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="paymentBoth"
                        >
                          Both
                        </label>
                      </div>

                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="paymentMethod"
                          id="paymentCredit"
                          value="B"
                          checked={paymentMethod === "B"}
                          onChange={() => handlePaymentMethodChange("B")}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="paymentCredit"
                        >
                          Credit
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-12  d-block justify-content-between">
                    {/* Amount Input - Cash Only */}
                    {paymentMethod === "C" && (
                      <div className="mb-2 d-flex">
                        <label className="form-label fw-bold col-6 col-md-5 col-lg-5">
                          Cash Amount : <span className="red-error">*</span>
                        </label>

                        <input
                          type="number"
                          className="form-control"
                          placeholder="Enter cash amount"
                          value={cashAmount}
                          onChange={(e) => setCashAmount(e.target.value)}
                          min="0"
                          onKeyDown={(e) => {
                            // Block arrow keys
                            if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onKeyPress={(e) => {
                            const isNumberKey = (event) => {
                              return (
                                event.key === "Backspace" ||
                                event.key === "Tab" ||
                                event.key === "ArrowLeft" ||
                                event.key === "ArrowRight" ||
                                event.key === "." ||
                                (event.key >= "0" && event.key <= "9")
                              );
                            };

                            if (!isNumberKey(e)) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => {
                            // Prevent scrolling to change the value
                            e.target.blur();
                          }}
                        />

                        {cashError && (
                          <p className="red-error mb-0">{cashError}</p>
                        )}
                      </div>
                    )}

                    {/* Amount Input - Online Only */}
                    {paymentMethod === "O" && (
                      <div className="mb-2 d-flex">
                        <label className="form-label fw-bold col-6 col-md-5 col-lg-5">
                          Online Amount : <span className="red-error">*</span>
                        </label>

                        <input
                          type="number"
                          ref={onlineRef}
                          className="form-control"
                          placeholder="Enter online amount"
                          value={onlineAmount}
                          onChange={(e) => setOnlineAmount(e.target.value)}
                          min="0"
                          onKeyDown={(e) => {
                            // Block arrow keys
                            if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onKeyPress={(e) => {
                            const isNumberKey = (event) => {
                              return (
                                event.key === "Backspace" ||
                                event.key === "Tab" ||
                                event.key === "ArrowLeft" ||
                                event.key === "ArrowRight" ||
                                event.key === "." ||
                                (event.key >= "0" && event.key <= "9")
                              );
                            };

                            if (!isNumberKey(e)) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => {
                            // Prevent scrolling to change the value
                            e.target.blur();
                          }}
                        />

                        {onlineError && (
                          <p className="red-error mb-0">{onlineError}</p>
                        )}
                      </div>
                    )}

                    {/* Amount Inputs - Both */}
                    {paymentMethod === "M" && (
                      <>
                        <div className="mb-2 d-flex">
                          <label className="form-label fw-bold col-6 col-md-5 col-lg-5">
                            Cash Amount : <span className="red-error">*</span>
                          </label>

                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter cash amount"
                            value={cashAmount}
                            onChange={(e) => setCashAmount(e.target.value)}
                            min="0"
                            onKeyDown={(e) => {
                              // Block arrow keys
                              if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onKeyPress={(e) => {
                              const isNumberKey = (event) => {
                                return (
                                  event.key === "Backspace" ||
                                  event.key === "Tab" ||
                                  event.key === "ArrowLeft" ||
                                  event.key === "ArrowRight" ||
                                  event.key === "." ||
                                  (event.key >= "0" && event.key <= "9")
                                );
                              };

                              if (!isNumberKey(e)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => {
                              // Prevent scrolling to change the value
                              e.target.blur();
                            }}
                          />

                          {cashError && (
                            <p className="red-error mb-0">{cashError}</p>
                          )}
                        </div>

                        <div className="mb-2 d-flex">
                          <label className="form-label fw-bold col-6 col-md-5 col-lg-5">
                            Online Amount : <span className="red-error">*</span>
                          </label>

                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter online amount"
                            value={onlineAmount}
                            onChange={(e) => setOnlineAmount(e.target.value)}
                            min="0"
                            onKeyDown={(e) => {
                              // Block arrow keys
                              if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onKeyPress={(e) => {
                              const isNumberKey = (event) => {
                                return (
                                  event.key === "Backspace" ||
                                  event.key === "Tab" ||
                                  event.key === "ArrowLeft" ||
                                  event.key === "ArrowRight" ||
                                  event.key === "." ||
                                  (event.key >= "0" && event.key <= "9")
                                );
                              };

                              if (!isNumberKey(e)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => {
                              // Prevent scrolling to change the value
                              e.target.blur();
                            }}
                          />

                          {onlineError && (
                            <p className="red-error mb-0">{onlineError}</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <hr className="text-white mx-0" />
                </>
              )}
              {otpVerify !== "Y" && (
                <>
                  <div className="col-12 d-md-flex d-block">
                    <div className="mb-1 col-md-8 d-flex align-items-center">
                      <label className="form-label fw-bold col-md-4 col-lg-4 col-5 mt-2">
                        Enter OTP : <span className="red-error">*</span>
                      </label>

                      <div className="position-relative" style={{ flex: 1 }}>
                        <input
                          ref={otpRef}
                          type={showOtp ? "text" : "password"}
                          className="form-control"
                          style={{
                            paddingRight: "45px",
                            width: "100%",
                          }}
                          placeholder="Enter OTP"
                          value={otp}
                          maxLength={6}
                          onChange={(e) => setOTP(e.target.value)}
                          onKeyDown={(e) => {
                            if (["ArrowUp", "ArrowDown"].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          onKeyPress={(e) => {
                            if (!(e.key >= "0" && e.key <= "9")) {
                              e.preventDefault();
                            }
                          }}
                          onWheel={(e) => e.target.blur()}
                        />

                        <button
                          type="button"
                          className="otp_eye_container"
                          onClick={() => setShowOtp((prev) => !prev)}
                          aria-label={showOtp ? "Hide OTP" : "Show OTP"}
                        >
                          <span className="material-icons">
                            {showOtp ? "visibility_off" : "visibility"}
                          </span>
                        </button>
                      </div>
                    </div>

                    {otpError && (
                      <p className="d-md-none d-lg-none red-error text-end m-0 ">
                        {otpError}
                      </p>
                    )}
                    <div className="mb-1 col-md-4 col-lg-4 col-12 text-end">
                      <ButtonSpinner
                        type="submit"
                        className="col-md-9 col-7 btn-theme text-black bg-gradient-info btn-md "
                        loading={otpLoading}
                        onClick={handleVerifyOTP}
                      >
                        <b>{loading ? "Verifying OTP..." : "Very OTP"}</b>
                      </ButtonSpinner>
                    </div>
                  </div>
                  {otpError && (
                    <p className="d-none d-md-flex red-error m-0">{otpError}</p>
                  )}
                  {/* Send OTP link */}
                  <div className="mt-1 text-start">
                    <span
                      className="otp-link text-decoration-underline"
                      style={{ cursor: "pointer", fontSize: "16px" }}
                      onClick={handleSendOtp}
                    >
                      Send OTP
                    </span>
                  </div>
                </>
              )}
              {otpVerify === "Y" && (
                <div className="d-flex flex-column align-items-center justify-content-center text-center">
                  <div className="text-success">
                    OTP Verified Successfully !
                  </div>
                </div>
              )}
              <hr className="text-white mx-0" />
              <div className="d-flex gap-2">
                {/* Camera */}
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  id="cameraInput"
                  hidden
                  onChange={handleImageChange}
                />

                <button
                  className="btn btn-outline-theme"
                  onClick={() => document.getElementById("cameraInput").click()}
                >
                  📷 Take Photo
                </button>

                {/* Gallery */}
                {/* <input
                  type="file"
                  accept="image/*"
                  id="galleryInput"
                  hidden
                  onChange={handleImageChange}
                />

                <button
                  className="btn btn-outline-theme"
                  onClick={() =>
                    document.getElementById("galleryInput").click()
                  }
                >
                  🖼️ Choose from Gallery
                </button> */}
              </div>
              {imageError && <p className="red-error">{imageError}</p>}

              {preview && (
                <div className="d-flex justify-content-center">
                  <img
                    src={preview}
                    alt="Preview"
                    className="img-fluid mt-2 rounded "
                    style={{ maxHeight: "200px" }}
                  />
                </div>
              )}
            </div>

            {/* Remark */}
            <div className="mb-1">
              <label className="form-label fw-bold">
                Remark : <span className="red-error">*</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Enter delivery remark..."
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
              />
              {remarkError && <p className="red-error">{remarkError}</p>}
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="danger" onClick={handleCloseDeliverySubmit}>
              Cancel
            </Button>

            <Button
              variant="success"
              onClick={handleSubmitDeliveryRemark}
              disabled={otpVerify !== "Y"}
            >
              Submit
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title className="text-theme">Success Alert</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-column align-items-center justify-content-center text-center">
              <Image
                width={55}
                height={50}
                src="/assest/green-tick.png"
                alt="success_icon"
                className="mb-2"
              />

              <div className="text-success">
                Order Delivered Successfully !
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="success"
              className="btn btn-success  "
              onClick={() => {
                getReadyDeliveryOrders();
                setShowSuccessModal(false);
              }}
            >
              Ok
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <Modal
        show={showLogoutModal}
        onHide={closeModal}
        backdrop="static"
        keyboard={false}
      >
        <div className="bg-Modal-black">
          <Modal.Header closeButton>
            <Modal.Title className="text-theme">Logout Alert</Modal.Title>
          </Modal.Header>
          <Modal.Body>Are you sure you want to logout?</Modal.Body>
          <Modal.Footer>
            <Button
              variant="danger"
              ref={no_ref}
              onClick={closeModal}
              className="no-btn"
            >
              No
            </Button>
            <Button
              variant="success"
              className="btn btn-success  "
              onClick={logout}
            >
              Yes
            </Button>
          </Modal.Footer>
        </div>
      </Modal>
    </>
  );
};
export default DeliveryDashboard;
